// Shared constants for the threshold-form builder.
//
// These used to live in GenerateTemplatePopup.tsx. They were extracted so the
// new drag-and-drop builder canvas can import them without creating a circular
// dependency (GenerateTemplatePopup -> builder/* -> docOvelayTool -> ...).
// GenerateTemplatePopup re-exports both for backward compatibility.

export const TEMPLATE_KEY = 'templateData';
export const SIGNATURE = 'signature';

// Lines a wrapped ("multiline") field may use before it starts shrinking rather
// than growing. Lives here rather than in docOvelayTool so builder/elements can
// read it without pulling docOvelayTool back into the cycle.
export const DEFAULT_MAX_LINES = 3;

// Point size used when neither the field nor the form says otherwise.
export const BASE_FONT_PT = 12;

// The form's own text size, stored at the ROOT of the markup beside `completed`.
export const FORM_FONT_KEY = 'fontSize';

/**
 * True for a markup root entry that holds fields — i.e. a page.
 *
 * The root holds pages ('0', '1', …) alongside plain metadata: `completed`, and
 * now the form's text size. Several loops used to treat every root key as a
 * page, so a scalar sitting there was iterated as if it held fields — harmless
 * for `completed` by luck, but it would have made overlayInfo emit a stray blank
 * page per scalar and rebaseAnswers replace the value with `{}`.
 *
 * Keyed off the VALUE rather than the key's shape. Real pages are always '0',
 * '1', … but a form whose pages were named some other way would silently stop
 * rendering under a digits-only rule, and metadata is a scalar either way.
 */
export const isPageEntry = (value: any): boolean =>
  !!value && typeof value === 'object' && !Array.isArray(value);

export const isPageKey = (key: string, markup?: Record<string, any>): boolean =>
  (markup ? isPageEntry(markup[key]) : /^\d+$/.test(`${key}`));

/** Page keys in page order — `Object.keys` order is not worth relying on. */
export const pageKeysOf = (markup: Record<string, any>): string[] => {
  const keys = Object.keys(markup || {}).filter(k => isPageEntry(markup[k]));
  // Numeric page keys sort by page number; anything else keeps its own order.
  const allNumeric = keys.every(k => /^\d+$/.test(k));
  return allNumeric ? keys.sort((a, b) => Number(a) - Number(b)) : keys;
};

/**
 * Total placed fields across the whole markup.
 *
 * Counts FIELDS, not root keys — the distinction is load-bearing. The builder
 * seeds one entry per page image, so a form that reached the clean-slate branch
 * produces `{"0":{},"1":{},"2":{},"3":{}}`: four root keys, zero placements. Any
 * check that asks "is this markup empty?" by looking at root keys calls that
 * populated, which is how a fieldless stash came to outrank a form's real
 * markup. `completed` is bookkeeping rather than a placement, so it is excluded
 * too, and root scalars (`completed`, the form's text size) are not pages.
 */
export const fieldCount = (markup: any): number =>
  Object.values(markup || {}).reduce(
    (n: number, page: any) =>
      n + (isPageEntry(page) ? Object.keys(page).filter(k => k !== 'completed').length : 0),
    0,
  );

/** The form-wide text size in points; a field may still override it. */
export const formFontPt = (markup: any): number => {
  const pt = Number(markup && markup[FORM_FONT_KEY]);
  return Number.isFinite(pt) && pt > 0 ? pt : BASE_FONT_PT;
};

/** A field's effective size: its own if set, otherwise the form's. */
export const fieldFontPt = (item: any, globalPt: number = BASE_FONT_PT): number => {
  const pt = Number(item && item.fontSize);
  return Number.isFinite(pt) && pt > 0 ? pt : globalPt;
};
